import Game from '../Game';
import { IGameState } from '../State/Game/IGameState';
import { initialPlayerState, IPlayerState } from '../State/Player/IPlayerState';
import DateHelper from '../Utility/DateHelper';
import IPlayerData from './IPlayerData';
import DataRestoreReward from './PlayerProperty/Other/DataRestoreReward';
import DataRestoreCounter from './PlayerProperty/Other/DataRestoreCounter';
import FTUE from './PlayerProperty/Other/FTUE';
import Inventory from './PlayerProperty/Other/Inventory';
import BotOptIn from './PlayerProperty/Other/BotOptIn';
import CardBackTexture from './PlayerProperty/Other/CardBackTexture';
import CardBackTextures from './PlayerProperty/Other/CardBackTextures';
import Friends from './PlayerProperty/Other/Friends';
import GateFriends from './PlayerProperty/Other/GateFriends';
import LifeShareCount from './PlayerProperty/Other/LifeShareCount';
import Missions from './PlayerProperty/Other/Missions';
import ReceivedMessages from './PlayerProperty/Other/ReceivedMessages';
import ShortcutReward from './PlayerProperty/Other/ShortcutReward';
import Unlocks from './PlayerProperty/Other/Unlocks';
import VolumeMusic from './PlayerProperty/Other/VolumeMusic';
import VolumeSound from './PlayerProperty/Other/VolumeSound';
import WheelSpins from './PlayerProperty/Other/WheelSpins';
import PlayerProperty from './PlayerProperty/PlayerProperty';
import Coins from './PlayerProperty/Stats/Coins';
import Keys from './PlayerProperty/Stats/Keys';
import Levels from './PlayerProperty/Stats/Levels';
import Lives from './PlayerProperty/Stats/Lives';
import World from './PlayerProperty/Stats/World';
import FoxTimer from './PlayerProperty/Timers/FoxTimer';
import GateTimer from './PlayerProperty/Timers/GateTimer';
import KeyMissionTimer from './PlayerProperty/Timers/KeyMissionTimer';
import LifeSharePrizeTimer from './PlayerProperty/Timers/LifeSharePrizeTimer';
import LifeTimer from './PlayerProperty/Timers/LifeTimer';
import MissionTimer from './PlayerProperty/Timers/MissionTimer';
import WheelTimer from './PlayerProperty/Timers/WheelTimer';
import Purchases from './PlayerProperty/Other/Purchases';
import Flags from './PlayerProperty/Other/Flags';
import GDPRCompliance from './PlayerProperty/Other/GDPRCompliance';
import PushNotifications from './PlayerProperty/platform/PushNotifications';
import { AsyncActionStatus } from '../State/AsyncAction';

type PlayerProperties = {
    [P in keyof IPlayerState]: PlayerProperty<
        IPlayerState[P],
        Partial<IPlayerData>
    >;
};

/**
 * Create player state from saved data
 */
export function createPlayerState(savedData: IPlayerData): IPlayerState {
    // Deep copy initial player state to avoid mutation
    const state = JSON.parse(JSON.stringify(initialPlayerState));
    const keys = Object.keys(initialPlayerState) as Array<keyof IPlayerState>;
    keys.forEach(prop => {
        const property = Game.player.data.properties[prop];
        if (property) {
            state[prop] = property.restore(savedData);
        }
    });
    return state as IPlayerState;
}

export default class DataManager {
    public playerEndpointData: IPlayerData;

    public properties: PlayerProperties;

    /**
     * This sub-set of properties will instantly trigger a data save when changed, instead of waiting for the interval.
     */
    public forceSaveProps: Array<keyof PlayerProperties>;

    private isDirty: boolean = false;

    private isSaving: boolean = false;

    private isStarted: boolean = false;

    constructor() {
        this.properties = {
            coins: new Coins(),
            lives: new Lives(),
            keys: new Keys(),
            world: new World(),
            levels: new Levels(),
            gateFriends: new GateFriends(),
            receivedMessageIDs: new ReceivedMessages(),
            inventory: new Inventory(),
            backcard: new CardBackTexture(),
            backcards: new CardBackTextures(),
            missions: new Missions(),
            wheelSpins: new WheelSpins(),
            friendData: new Friends(),
            lifeShareCount: new LifeShareCount(),
            playerUnlocks: new Unlocks(),
            dataRestoreCounter: new DataRestoreCounter(),
            lifeTimer: new LifeTimer(),
            gateTimer: new GateTimer(),
            missionTimer: new MissionTimer(),
            keyMissionTimer: new KeyMissionTimer(),
            lifeSharePrizeTimer: new LifeSharePrizeTimer(),
            wheelTimer: new WheelTimer(),
            foxTimer: new FoxTimer(),

            volumeMusic: new VolumeMusic(),
            volumeSFX: new VolumeSound(),

            pushNotifications: new PushNotifications(),
            gdprCompliance: new GDPRCompliance(),

            optInReward: new BotOptIn(),
            dataRestoreReward: new DataRestoreReward(),
            shortcutReward: new ShortcutReward(),

            purchases: new Purchases(),
            FTUE: new FTUE(),
            flags: new Flags(),

            // Meta
            activeWorld: null,
            previousCoinsValue: null,
            tasks: null,
            spinWheelTask: null,
            spinWheelRewardType: null,
        };

        this.forceSaveProps = [
            'coins',
            'lives',
            'levels',
            'inventory',
            'gateFriends',
            'volumeMusic',
            'volumeSFX',
            'backcard',
            'missions',
            'wheelTimer',
            'pushNotifications',
            'gdprCompliance',
        ];
    }

    public start() {
        this.isStarted = true;
        window.setInterval(() => {
            this.flush(Game.state.getState());
        }, Game.config.dataSaveIntervalMS);
    }

    /**
     * Fetch all data from the SDK and store in memory.
     */
    public async restore() {
        this.playerEndpointData = await Game.sdk.restore();
    }

    public getPlayerState(): IPlayerState {
        // prettier-ignore
        return {
            coins: this.properties.coins.restore(this.playerEndpointData),
            lives: this.properties.lives.restore(this.playerEndpointData),
            keys: this.properties.keys.restore(this.playerEndpointData),
            world: this.properties.world.restore(this.playerEndpointData),
            levels: this.properties.levels.restore(this.playerEndpointData),
            gateFriends: this.properties.gateFriends.restore(this.playerEndpointData),
            receivedMessageIDs: this.properties.receivedMessageIDs.restore(this.playerEndpointData),
            inventory: this.properties.inventory.restore(this.playerEndpointData),
            backcard: this.properties.backcard.restore(this.playerEndpointData),
            backcards: this.properties.backcards.restore(this.playerEndpointData),
            missions: this.properties.missions.restore(this.playerEndpointData),
            wheelSpins: this.properties.wheelSpins.restore(this.playerEndpointData),
            friendData: this.properties.friendData.restore(this.playerEndpointData),
            lifeShareCount: this.properties.lifeShareCount.restore(this.playerEndpointData),
            playerUnlocks: this.properties.playerUnlocks.restore(this.playerEndpointData),
            dataRestoreCounter: this.properties.dataRestoreCounter.restore(this.playerEndpointData),
            lifeTimer: this.properties.lifeTimer.restore(this.playerEndpointData),
            gateTimer: this.properties.gateTimer.restore(this.playerEndpointData),
            missionTimer: this.properties.missionTimer.restore(this.playerEndpointData),
            keyMissionTimer: this.properties.keyMissionTimer.restore(this.playerEndpointData),
            lifeSharePrizeTimer: this.properties.lifeSharePrizeTimer.restore(this.playerEndpointData),
            wheelTimer: this.properties.wheelTimer.restore(this.playerEndpointData),
            foxTimer: this.properties.foxTimer.restore(this.playerEndpointData),
            volumeMusic: this.properties.volumeMusic.restore(this.playerEndpointData),
            volumeSFX: this.properties.volumeSFX.restore(this.playerEndpointData),
            pushNotifications: this.properties.pushNotifications.restore(this.playerEndpointData),
            gdprCompliance: this.properties.gdprCompliance.restore(this.playerEndpointData),
            optInReward: this.properties.optInReward.restore(this.playerEndpointData),
            dataRestoreReward: this.properties.dataRestoreReward.restore(this.playerEndpointData),
            shortcutReward: this.properties.shortcutReward.restore(this.playerEndpointData),

            purchases: this.properties.purchases.restore(this.playerEndpointData),
            FTUE: this.properties.FTUE.restore(this.playerEndpointData),
            flags: this.properties.flags.restore(this.playerEndpointData),

            activeWorld : this.properties.world.restore(this.playerEndpointData),
            previousCoinsValue: this.properties.coins.restore(this.playerEndpointData),
            tasks: [],
            spinWheelTask: [],
            spinWheelRewardType: null,
        };
    }

    public handleStateChanged(state: IGameState) {
        if (this.isStarted) {
            if (
                this.forceSaveProps
                    .map(propKey => this.properties[propKey])
                    .some(
                        prop =>
                            prop &&
                            prop.isChanged(
                                state.playerState,
                                this.playerEndpointData
                            )
                    )
            ) {
                this.flush(state, true);
            } else if (!this.isDirty) {
                this.isDirty = Object.values(this.properties).some(
                    prop =>
                        prop &&
                        prop.isChanged(
                            state.playerState,
                            this.playerEndpointData
                        )
                );
            }
        }
    }

    /**
     * Save any changed data in the specified player state to the SDK.
     * @param playerStateData The state object to compare against cached data to determine the change-set.
     * @param force Immediately save changes, ignore `isDirty` flag.
     */
    public async flush(state: IGameState, force: boolean = false) {
        const { playerState: playerStateData, dataConflict } = state;
        // Do not save if there is a conflict resolution in progress.
        if (dataConflict.status === AsyncActionStatus.IN_PROGRESS) return;
        // Do not save if there is a save already in progress.
        if (this.isSaving) return;

        // Check if the data is dirty or the `force` flag is true.
        if (force === false && !this.isDirty) return;

        // Immediately set isDirty flag to false to allow for dirtyificiation to take
        // place during the next save process.
        this.isDirty = false;

        // Set isSaving flag to true to prevent multiple save processes from running at the same time.
        this.isSaving = true;

        // Object.assign + spread to merge partials.
        const saveData: Partial<IPlayerData> = Object.assign(
            {},
            ...Object.values(this.properties)
                .filter(
                    prop =>
                        prop &&
                        prop.isChanged(playerStateData, this.playerEndpointData)
                )
                .map(prop => prop.save(playerStateData))
        );

        // Set a timestamp for data-integrity checking.
        saveData.data_timestamp = DateHelper.now().toString();

        await Game.sdk.save(saveData);
        await Game.sdk.flushSaveData();

        // Update playerEndpointData with changes so we know if anything changes again.
        this.playerEndpointData = {
            ...this.playerEndpointData,
            ...saveData,
        };

        this.isSaving = false;
    }
}
